<script setup lang="ts">import { useUserStore as ___useUserStore } from "@client/stores"; const $$user =  ___useUserStore();import { $api } from "@client/lib/trpc";import { $socket } from "@client/lib/socket";
import { SearchIcon, UsersIcon, ArrowLeftIcon, XIcon } from "lucide-vue-next";

import { useDebounce } from "@client/composables/debounceFn";
import BaseSocial from "./Base.vue";
const activeComponent = defineModel<string>({ default: "List" });

$$user.friendList = await $api.friends.list.query();
const isSearching = ref(false),
	searchedValue = ref(""),
	searchResults = ref([] as typeof $$user.friendList);

const friendListTarget = computed(() =>
	!isSearching.value ? $$user.friendList : searchResults.value,
);

const handleSearchDebounced = useDebounce(
	async () => {
		if (!searchedValue.value.trim()) return;
		searchResults.value = [];
		searchResults.value = await $api.friends.search.query({
			username: searchedValue.value,
		});
	},
	1000,
	{ maxWait: 5000 },
);

function handleFriendsManagerClicked() {
	activeComponent.value = "Manager";
}

function handleFriendClicked(clickedfriendId: number) {
	$socket.social.emit(
		"getFriendsStatus",
		$$user.friendList.map((user) => user.id),
		(ids) => {
			for (const id in ids) {
				const friend = $$user.friendList.find((user) => user.id === Number(id));
				if (friend) friend.status = ids[id];
				if (id === clickedfriendId.toString() && ids[id] !== "offline") {
					$$user.activeChatId = clickedfriendId;
					activeComponent.value = "Chat";
				}
			}
		},
	);
}

async function handleDeleteFriend(friendId: number) {
	await $api.friends.remove.mutate(friendId);
	$$user.friendList = $$user.friendList.filter((friend) => friend.id !== friendId);
	if (isSearching.value)
		searchResults.value = searchResults.value.filter((friend) => friend.id !== friendId);
}
function handleBack() {
	isSearching.value = false;
	searchedValue.value = "";
	searchResults.value = [];
}

function getStatusColor(status: "online" | "offline" | "ingame") {
	switch (status) {
		case "online":
			return "tw:text-green-600";
		case "ingame":
			return "tw:text-blue-600";
		case "offline":
			return "tw:text-white";
	}
}
</script>
<template>
	<BaseSocial>
		<template v-if="isSearching" #prepend-title>
			<ArrowLeftIcon
				class="tw:self-center tw:justify-self-center tw:hover:scale-110 tw:cursor-pointer tw:shrink-0"
				@click="handleBack" />
		</template>
		<template #title>
			<p
				v-if="!isSearching"
				class="tw:text-white tw:text-[max(16px,1vmax)] tw:self-center tw:font-semibold tw:w-full">
				Social
			</p>
			<v-text-field
				v-else
				v-model="searchedValue"
				@update:model-value="handleSearchDebounced"
				autofocus
				variant="solo"
				hide-details>
				<template #append-inner>
					<SearchIcon class="tw:max-md:hidden" :size="18" />
				</template>
			</v-text-field>
		</template>
		<template #append>
			<div v-if="!isSearching">
				<SearchIcon
					class="tw:cursor-pointer tw:inline"
					@click="isSearching = !isSearching"
					:size="18" />

				<div class="tw:inline tw:relative tw:px-1">
					<UsersIcon
						@click="handleFriendsManagerClicked"
						class="tw:cursor-pointer tw:inline"
						:size="18" />

					<span
						v-if="$$user.hasIncomingRequests"
						class="tw:bg-red-500 tw:absolute tw:text-[5.5px] tw:flex tw:justify-center tw:items-center tw:rounded-full
							tw:left-1/2 tw:top-1/2 tw:aspect-square tw:w-3">
						1+
					</span>
				</div>
			</div>
		</template>
		<template #content>
			<ul class="tw:flex tw:flex-col tw:gap-3 tw:overflow-auto tw:p-3">
				<li
					@click="handleFriendClicked(friend.id)"
					v-for="friend in friendListTarget"
					:key="friend?.id"
					class="tw:group tw:flex tw:gap-2 tw:items-center tw:justify-between tw:p-2 tw:rounded-md
						tw:hover:bg-neutral-700 tw:hover:cursor-pointer">
					<div class="tw:flex tw:items-center tw:gap-2">
						<div class="tw:relative">
							<img
								class="tw:w-8 tw:rounded-full tw:object-cover tw:aspect-square"
								:src="friend?.avatarUrl!" />
							<span
								v-if="$$user.pendingChatIds.has(friend.id)"
								class="tw:bg-red-500 tw:absolute tw:text-[6px] tw:flex tw:justify-center tw:items-center tw:rounded-sm
									tw:right-0 tw:bottom-0 tw:aspect-square tw:w-[max(6px,0.5vmax)]">
								1+
							</span>
						</div>
						<div class="tw:flex tw:flex-col tw:leading-tight tw:overflow-hidden">
							<p class="tw:truncate tw:text-[max(12px,0.8vmax)] tw:font-medium">
								{{ friend?.username }}
							</p>
							<p
								:class="getStatusColor(friend.status)"
								class="tw:truncate tw:text-[max(10px,0.5vmax)] tw:capitalize">
								{{ friend.status }}
							</p>
						</div>
					</div>
					<XIcon
						v-confirm-click="{
							message: `Are you sure you want to delete ${friend.username}?`,
							handler: async () => await handleDeleteFriend(friend.id),
						}"
						:size="16"
						color="red"
						title="Delete"
						class="tw:hover:scale-110 tw:!invisible tw:group-hover:!visible" />
				</li>
			</ul>
		</template>
	</BaseSocial>
</template>
